// Enums and domain types (architecture §7.2, verbatim).
import type { Card } from './cards.ts';

export type ModelId = 'claude-opus-5-5' | 'claude-sonnet-5-5' | 'claude-haiku-4-5' | 'claude-fable-5-1';
export type Effort = 'low' | 'medium' | 'high' | 'xhigh' | 'max';
export type Permission = 'auto' | 'freigabe' | 'aus';
export type AgentStatus = 'aktiv' | 'pausiert' | 'archiviert';
export interface AgentAppearance { hue?: number; glyph?: string; initials?: string }
export interface WorkingHours { tz: 'Europe/Berlin'; days: number[]; from: string; to: string } // days 1=Mo..7=So, "08:00"
export type AgentLiveStatus =
  | { state: 'frei' }
  | { state: 'arbeitet'; runId: string; threadId: string; what: string; since: string }
  | { state: 'wartet'; on: 'freigabe' | 'chris'; count: number }
  | { state: 'pausiert' }
  | { state: 'budget' };

export type ThreadKind = 'chat' | 'meeting' | 'task' | 'job_run' | 'call';
export interface MeetingState { rounds: number; moderatorAgentId: string | null; round: number; queue: string[]; triggerMessageId: string | null }
export type SegmentUse = 'chat' | 'job' | 'task' | 'meeting' | 'voice';
export type SegmentHeadKind = 'keiner' | 'compaction' | 'zusammenfassung';

export type MessageRole = 'user' | 'assistant' | 'system';
export type AuthorKind = 'chris' | 'agent' | 'app';
export type MessageOrigin = 'app' | 'voice' | 'job' | 'event' | 'context' | 'import' | 'push';
export type MessageStatus = 'fertig' | 'abgelehnt' | 'fehler';
export type AppEventKind = 'freigabe_ergebnis' | 'anruf_zusammenfassung' | 'aufgabe' | 'bericht_antwort' | 'erinnerung' | 'gedaechtnis' | 'import' | 'system';
export type PendingKind = AppEventKind | 'chris_nachricht';
export interface QuickReplyOption { id: string; label: string; action?: 'reply' | 'job_ack' | 'task_answer' | 'sonstiges' }
export type MessagePart =
  | { type: 'text'; md: string }
  | { type: 'progress'; text: string }                        // display:"updates" progress line (kept for the run detail; UI may collapse)
  | { type: 'tool'; toolUseId: string; name: string; label: string; status: 'ok' | 'fehler' | 'freigabe'; summary: string }
  | { type: 'server_tool'; name: string; ok: boolean; query?: string }
  | { type: 'card'; card: Card }
  | { type: 'attachment'; attachmentId: string }
  | { type: 'quick_replies'; options: QuickReplyOption[]; answeredOptionId: string | null }
  | { type: 'event'; event: AppEventKind; title: string; body: string; ref?: Record<string, string> }
  | { type: 'fallback'; from: string; to: string }
  | { type: 'refusal'; category: string | null; explanation: string | null };

export type RunTrigger = 'chat' | 'job' | 'task' | 'meeting' | 'voice' | 'approval' | 'webhook' | 'briefing' | 'reminder';
/** runs.meta (§4.2 runs.ts). */
export interface RunMeta {
  inputMessageId?: string;          // step 4 wrote this run's input; never written twice (§10.2)
  chrisMessageId?: string;          // the Chris message this run answers, if any (approval herkunft, §10.9)
  tainted?: boolean;                // run took in third-party data (§10.7)
  chainDepth?: number;              // agent-initiated follow-up depth (§10.10)
  fireKey?: string;                 // `${jobId}:${scheduledFor ISO minute}` (§10.13)
  budgetOverride?: boolean;         // "trotzdem senden" for every call of this run only (§10.11)
  attempts?: number;
  selfReminder?: boolean;           // started by an agent's own reminder: may not set another one (§10.10)
  triggerKey?: string;              // integration trigger runs: `${jobId}:${gmailThreadId ?? resourceId}` (overlap per resource, §10.13)
  restartAfterDeploy?: boolean;     // aborted by drain(); re-enqueued like recover() (§10.2)
  restartOf?: string;               // run id this run restarts after drain or recovery
}
export type RunStatus = 'queued' | 'running' | 'done' | 'waiting_approval' | 'needs_continue' | 'refused' | 'error' | 'aborted' | 'budget_stopped';
export interface RunError { code: string; messageDe: string; retryable: boolean; requestId?: string }
export type StepKind = 'api' | 'tool' | 'server_tool' | 'mcp' | 'freigabe' | 'fallback' | 'thinking_dropped' | 'hinweis' | 'fehler';

export type TaskStatus = 'eingang' | 'geplant' | 'in_arbeit' | 'wartet' | 'erledigt' | 'abgebrochen';
/** The statuses of an open task (not erledigt, not abgebrochen). */
export const OPEN_TASK_STATUSES: readonly TaskStatus[] = ['eingang', 'geplant', 'in_arbeit', 'wartet'];
export type TaskPriority = 'niedrig' | 'normal' | 'hoch' | 'dringend';
export interface TaskQuestion { text: string; options: QuickReplyOption[]; askedAt: string; messageId: string | null }
export type TaskEventKind = 'erstellt' | 'status' | 'kommentar' | 'zugewiesen' | 'fortschritt' | 'frage' | 'antwort' | 'ergebnis' | 'faellig';

export type ApprovalStatus = 'vorbereitung' | 'offen' | 'wird_ausgefuehrt' | 'ausgefuehrt' | 'abgelehnt' | 'fehlgeschlagen' | 'abgelaufen' | 'zurueckgezogen';
export type ApprovalRisk = 'niedrig' | 'mittel' | 'hoch' | 'geld';
export interface ApprovalPreview {
  title: string; summary: string; card?: Card;
  diff?: Array<{ label: string; before: string | null; after: string }>;
  links?: Array<{ label: string; url: string }>;
  editable?: Array<{ path: string; label: string; kind: 'text' | 'html' | 'number' }>;
  countdownUntil?: string;                                   // money: STZ code expiry
  grund?: string;                                            // why the approval path was taken although the level is auto (guard, §11.5/§11.6)
  herkunft?: { trigger: RunTrigger; chrisNachricht: { messageId: string; auszug: string } | null }; // null → UI: "Kein Auftrag von Chris in diesem Lauf"
  warnungen?: string[];                                      // red hints (e.g. retoure_pruefen: doppelt_bestaetigt, Intern text found)
}

export type JobKind = 'cron' | 'einmalig' | 'ausloeser';
export type JobDelivery = 'bericht' | 'chat' | 'still';
export interface JobProvenance { source: 'routine' | 'vorlage' | 'chris' | 'agent'; triggerId?: string; routineName?: string; file?: string; adaptedOn?: string; needs?: IntegrationKey[]; erledigt?: boolean;
  tainted?: boolean }                                        // created or last changed by a tainted run: its runs start tainted (§10.7)

export type ReportKind = 'tagesbericht' | 'wochenbericht' | 'analyse' | 'rueckblick' | 'erinnerung' | 'fehler' | 'sonstiges';
export type MemoryKind = 'fakt' | 'praeferenz' | 'entscheidung' | 'person' | 'gelernt';
export type MemorySourceKind = 'chris' | 'agent' | 'import' | 'notion' | 'mail' | 'bericht_antwort'; // set by the server only; 'chris' only via POST /api/memories (§10.15)
export type MemoryStatus = 'aktiv' | 'archiviert' | 'vorschlag';
export interface MemorySourceRef { threadId?: string; messageId?: string; reportId?: string; url?: string; note?: string }

export type NotificationKind = 'info' | 'freigabe' | 'dringend' | 'anruf' | 'verpasster_anruf' | 'bericht' | 'aufgabe' | 'integration' | 'budget' | 'erinnerung';
export type Urgency = 'normal' | 'dringend' | 'anruf';
export type CallStatus = 'klingelt' | 'aktiv' | 'beendet' | 'verpasst' | 'abgelehnt' | 'fehlgeschlagen';
export type CallDirection = 'agent_ruft_chris' | 'chris_ruft_agent';
export type CallProvider = 'push' | 'twilio';
export type VoiceMode = 'gespraech' | 'ptt' | 'vorlesen' | 'basis';

export type IntegrationKey = 'anthropic' | 'shopify' | GmailIntegrationKey | 'notion' | 'elevenlabs' | 'webpush' | 'ntfy' | `mcp:${string}`;
export type IntegrationState = 'nicht_verbunden' | 'verbunden' | 'fehler' | 'getrennt' | 'deaktiviert';
export interface McpToolDef { name: string; description?: string; input_schema: Record<string, unknown>;
  annotations?: { readOnlyHint?: boolean; destructiveHint?: boolean; title?: string };
  defHash?: string }                                         // sha256(name + description + input_schema + annotations), set on refresh (§11.8)
/** Per model: valid for the model AND every fallback target (§10.5). Frozen per segment (segments.model_caps). */
export interface ModelCaps { systemMessages: boolean; perMessageEffort: boolean }
export type UsageProvider = 'anthropic' | 'elevenlabs';
export type UsagePurpose = 'chat' | 'job' | 'task' | 'meeting' | 'voice' | 'compaction' | 'summary' | 'prewarm' | 'utility' | 'diagnose' | 'stt' | 'tts';
export type CockpitTileKey = 'umsatz' | 'bestellungen' | 'warenkorb' | 'conversion' | 'top_produkte' | 'niedriger_bestand' | 'retouren' | 'support' | 'team' | 'kosten' | 'stimme';

export type StepUpPurpose = 'freigabe' | 'geraet' | 'passkey' | 'schluessel' | 'export' | 'import' | 'konfiguration';
export interface Counts { approvals: number; unreadThreads: number; unreadReports: number; notifications: number }

/**
 * The Gmail slots (§12.2, CR-3). The one source of truth: the Mailbox union, MAILBOXES, the `gmail:<slot>` integration keys and
 * every `postfach` enum derive from this constant. A closed list, not configurable: a new mailbox is a code change.
 */
export const MAILBOX_ADDRESSES = Object.freeze({ support: 'support@sterlingz.de', chris: 'chris@sterlingz.de', info: 'info@sterlingz.de' } as const);
export type Mailbox = keyof typeof MAILBOX_ADDRESSES;
/** All slots in a fixed order (support, chris, info); a non-empty tuple so it feeds `z.enum` directly. */
export const MAILBOXES = Object.freeze(Object.keys(MAILBOX_ADDRESSES)) as readonly [Mailbox, ...Mailbox[]];
export type GmailIntegrationKey = `gmail:${Mailbox}`;
export const GMAIL_INTEGRATION_KEYS: readonly GmailIntegrationKey[] = Object.freeze(MAILBOXES.map((m) => `gmail:${m}` as const));
export function isMailbox(v: unknown): v is Mailbox { return typeof v === 'string' && Object.hasOwn(MAILBOX_ADDRESSES, v); }
/** `gmail:<slot>` → slot; null for any other key. */
export function gmailSlotOf(key: string): Mailbox | null { return key.startsWith('gmail:') && isMailbox(key.slice(6)) ? key.slice(6) as Mailbox : null; }
/**
 * The mailbox of ours an address reaches, or null: compared without case and without a +tag, since Gmail delivers
 * info+presse@sterlingz.de to info@ (CR-3). Every own-address check uses it (replies, the poller, copies in other mailboxes).
 */
export function mailboxOfAddress(address: string): Mailbox | null {
  const a = address.trim().toLowerCase().replace(/^([^@+]+)\+[^@]*@/, '$1@');
  return MAILBOXES.find((m) => MAILBOX_ADDRESSES[m] === a) ?? null;
}
/**
 * info@ automation (CR-3, decided by Chris on 09.10.2026): info@ is his business mailbox, and by default no agent writes
 * there on its own. The setting lives in `integrations.config.infoAutomation` of `gmail:info`; 'aus' is the only mode
 * today, and any other stored value reads as 'aus'. The hook for later automated answers or invoice forwarding: a new
 * mode goes into this list and into the one place the server reads it (gmail/guards.ts `writeGate`).
 */
export const INFO_AUTOMATION_MODES = ['aus'] as const;
export type InfoAutomation = (typeof INFO_AUTOMATION_MODES)[number];
export type NotionPageKey = 'analyst_wissen' | 'nachschub' | 'produktionskalender' | 'infomaterial';
/** agents.meta (§4.2 agents.ts). Guards read it at execution time. */
export interface AgentMeta {
  mailboxes?: Mailbox[];                        // Gmail tools refuse any other postfach (§11.6)
  notionReadable?: NotionPageKey[];             // notion_read / notion_search (§11.7); missing = none
  notionWritable?: NotionPageKey[];             // notion_append (§11.7)
  notionIntern?: boolean;                       // may read "Intern" sections; never true for agents with customer-facing tools (§11.7)
  taskTargets?: string[];                       // allowed task_create `fuer` values (agent ids, 'chris'); missing = all (§10.10)
  usesAddress?: boolean;                        // Anrede line in the session context (Jarvis)
  isLead?: boolean;
  memoryPrivate?: boolean;                      // memory_save never with fuer_alle
  skill?: { name: string; description: string; sha256: string };
  seedHash?: string;
  seedMeta?: Omit<AgentMeta, 'skill' | 'seedHash' | 'seedMeta' | 'seedSeen'>; // the meta values the seed wrote last; an upgrade keeps keys Chris changed (§19, CR-3)
  seedSeen?: string;                            // the seed's text hash at the upgrade that last kept Chris' text: the next one names it only when the seed changed it again (CR-3)
}

/** Moved here from server tools/types.ts so shared views can use them (§11.1). */
export type ToolGroup = 'hq' | 'aufgaben' | 'meeting' | 'gedaechtnis' | 'berichte' | 'kommunikation' | 'karten'
  | 'shopify' | 'gmail' | 'notion' | 'stz' | 'mcp' | 'web';
/** Registry metadata for server tools and MCP connector tools (no ToolDef: they run on Anthropic's side). */
export interface ExternalToolMeta { name: string; label: string; group: 'web' | 'stz' | 'mcp'; description: string;
  integration: IntegrationKey | null; allowed: Permission[]; defaultPermission: Permission; risk: ApprovalRisk; money: boolean }
